import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, inject, input, signal } from '@angular/core';
import { IsActiveMatchOptions, NavigationEnd, Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { RippleModule } from 'primeng/ripple';
import { MenuItem } from 'primeng/api';
import { LayoutService } from '../service/layout.service';
import { BadgeModule } from 'primeng/badge';

/** Casamento de rota do menu: caminho exato, ignorando query/matrix/fragment. */
const OPCOES_ROTA_EXATA: IsActiveMatchOptions = { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' };

/** Item de menu (recursivo) do menu lateral: renderiza link/submenu, controla estado ativo e animação. */
@Component({
    selector: '[app-menuitem]',
    standalone: true,
    imports: [CommonModule, RouterModule, RippleModule, BadgeModule],
    host: {
        '[class.layout-root-menuitem]': 'root()',
        '[class.active-menuitem]': 'activeClass',
        '[class.app-menuitem-disabled]': 'disabledClass'
    },
    template: `
        <ng-container>
            @if (root() && item().visible !== false) {
                <div class="layout-menuitem-root-text">{{ item().label }}</div>
            }
            @if ((!item().routerLink || item().items) && item().visible !== false) {
                <a [attr.href]="item().url" (click)="itemClick($event)" [ngClass]="item().styleClass" [attr.target]="item().target" tabindex="0" pRipple>
                    <i [ngClass]="item().icon" class="layout-menuitem-icon"></i>
                    <span class="layout-menuitem-text">{{ item().label }}</span>
                    @if (item().badge) {
                        <p-badge class="layout-menuitem-badge" [value]="item().badge" />
                    }
                    @if (item().items) {
                        <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
                    }
                </a>
            }
            @if (item().routerLink && !item().items && item().visible !== false) {
                <a
                    (click)="itemClick($event)"
                    [ngClass]="item().styleClass"
                    [routerLink]="item().disabled ? null : item().routerLink"
                    [class.active-route]="rotaAtiva()"
                    [fragment]="item().fragment"
                    [queryParamsHandling]="item().queryParamsHandling"
                    [preserveFragment]="item().preserveFragment"
                    [skipLocationChange]="item().skipLocationChange"
                    [replaceUrl]="item().replaceUrl"
                    [state]="item().state"
                    [queryParams]="item().queryParams"
                    [attr.target]="item().target"
                    tabindex="0"
                    pRipple
                >
                    <i [ngClass]="item().icon" class="layout-menuitem-icon"></i>
                    <span class="layout-menuitem-text">{{ item().label }}</span>
                    @if (item().badge) {
                        <p-badge class="layout-menuitem-badge" [value]="item().badge" />
                    }
                    @if (item().items) {
                        <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
                    }
                </a>
            }

            @if (item().items && item().visible !== false) {
                <ul [@children]="submenuAnimation">
                    @for (child of item().items; track child; let i = $index) {
                        <li app-menuitem [item]="child" [index]="i" [parentKey]="key" [class]="child['badgeClass']"></li>
                    }
                </ul>
            }
        </ng-container>
    `,
    animations: [
        trigger('children', [
            state(
                'collapsed',
                style({
                    height: '0'
                })
            ),
            state(
                'expanded',
                style({
                    height: '*'
                })
            ),
            transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppMenuitem implements OnInit, OnDestroy {
    readonly item = input.required<MenuItem>();
    readonly index = input.required<number>();
    readonly root = input(false);
    readonly parentKey = input('');

    /** Indica se o item (ou um de seus descendentes) está ativo; atualizado por eventos do menu/rota. */
    readonly active = signal(false);

    /**
     * A rota deste item é a rota atual. Guardado em signal (e não delegado ao `routerLinkActive`)
     * porque a diretiva aplica a classe fora do ciclo de detecção: o destaque só aparecia no
     * próximo evento da tela. Signal notifica e a classe entra junto com a navegação.
     */
    readonly rotaAtiva = signal(false);

    key: string = '';

    private readonly router = inject(Router);
    private readonly layoutService = inject(LayoutService);
    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        this.layoutService.menuSource$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
            Promise.resolve(null).then(() => {
                if (value.routeEvent) {
                    this.active.set(value.key === this.key || value.key.startsWith(this.key + '-'));
                } else {
                    if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
                        this.active.set(false);
                    }
                }
            });
        });

        this.layoutService.resetSource$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.active.set(false);
        });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.atualizarRotaAtiva();
                if (this.item().routerLink) {
                    this.updateActiveStateFromRoute();
                }
            });
    }

    ngOnInit() {
        this.key = this.parentKey() ? this.parentKey() + '-' + this.index() : String(this.index());
        this.atualizarRotaAtiva();

        if (this.item().routerLink) {
            this.updateActiveStateFromRoute();
        }

        // Permite que itens de menu sejam exibidos expandidos por padrão
        // quando a propriedade customizada `expanded` estiver definida no item.
        // Aplica apenas para itens não-root que possuem filhos.
        if (!this.root() && this.item().items && (this.item() as MenuItem & { expanded?: boolean }).expanded === true) {
            this.active.set(true);
        }
    }

    /** Recalcula se a rota do item é a atual; itens sem `routerLink` nunca ficam marcados. */
    private atualizarRotaAtiva(): void {
        this.rotaAtiva.set(!this.item().disabled && this.rotaDoItemEstaAtiva());
    }

    /**
     * `routerLink` do menu é um **array de segmentos** (`['/configuracoes', 'divida-ativa', 'assinantes']`).
     * Comparar só o primeiro elemento testaria `/configuracoes`, que nunca casa exato — daí montar a
     * árvore de URL com o caminho inteiro antes de perguntar ao Router.
     */
    private rotaDoItemEstaAtiva(): boolean {
        const routerLink = this.item().routerLink;
        if (!routerLink) {
            return false;
        }
        const caminho = Array.isArray(routerLink) ? routerLink : [routerLink];
        return this.router.isActive(this.router.createUrlTree(caminho), OPCOES_ROTA_EXATA);
    }

    updateActiveStateFromRoute() {
        if (this.rotaDoItemEstaAtiva()) {
            this.layoutService.onMenuStateChange({ key: this.key, routeEvent: true });
        }
    }

    itemClick(event: Event) {
        // avoid processing disabled items
        if (this.item().disabled) {
            event.preventDefault();
            return;
        }

        // execute command
        if (this.item().command) {
            this.item().command!({ originalEvent: event, item: this.item() });
        }

        // toggle active state
        if (this.item().items) {
            this.active.update((ativo) => !ativo);
        }

        this.layoutService.onMenuStateChange({ key: this.key });
    }

    get submenuAnimation() {
        return this.root() ? 'expanded' : this.active() ? 'expanded' : 'collapsed';
    }

    get activeClass() {
        return this.active() && !this.root();
    }

    get disabledClass() {
        return !!this.item()?.disabled;
    }

    ngOnDestroy() {}
}
